import Divider from '@material-ui/core/Divider';
import { useRerender } from 'hooks/useRerender';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { Send } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import SlidingPane from 'react-sliding-pane';
import { Badge, Button, Col, Row, Spinner } from 'reactstrap';
import Icon from 'utility/icomoon';
import { UI_ONLY_MAX_LENGTHS } from 'utility/constants/dbFieldLengths';
import TextInput from 'views/formComponents/TextInput';
import { getStatusStyles } from 'views/screens/Reports/views/byUnit/reportScriptHistory/utils';
import Avatar from '../../../../../@core/components/avatar';
import * as authSelectors from '../../../../../redux/reducers/auth';
import useGetUnitScripts from '../../hooks/useGetUnitScripts';
import useInsertUnitScript from '../../hooks/useInsertUnitScript';
import { env } from '@src/env';

interface Props {
  unitId: number;
  unitName: string | undefined;
  isOpenSlidingPane: boolean;
  closeSlidingPaneSendScripts: any;
}

const SlidingPaneUnitSendScripts = (props: Props) => {
  const { unitId, unitName, isOpenSlidingPane, closeSlidingPaneSendScripts } = props;
  const { control, handleSubmit, watch, reset } = useForm();
  const intl = useIntl();
  const { rerender, startRerender } = useRerender();
  const authUserId = useSelector((state) => Number(authSelectors.getAuthUser(state).authUserId));
  const newScript = watch('script');
  const fetchScriptsInterval = useRef<any>();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [firstLoad, setFirstLoad] = useState(true);
  const [executeSave, setExecuteSave] = useState(false);
  const [scriptToSave, setScriptToSave] = useState<any>({});

  const primary = env.VITE_PRIMARY_COLOR;
  const secondary = env.VITE_SECONDARY_COLOR;

  const { scriptsChats, isFetchingScripts } = useGetUnitScripts(unitId, rerender);

  const { submittingSave } = useInsertUnitScript(executeSave, scriptToSave);

  const getScriptStatus = (status) => {
    const styles = getStatusStyles(status);
    if (status === null) {
      return null;
    }

    const { text, color, icon } = styles;

    return (
      <Badge pill color={`light-${color}`} style={{ fontSize: '12px' }}>
        <Icon icon={icon} className={'mr-25'} size={14} color={color} />
        {text}
      </Badge>
    );
  };

  const cleanup = () => {
    if (fetchScriptsInterval.current) clearInterval(fetchScriptsInterval.current);
  };

  useEffect(() => {
    return () => cleanup();
  }, []);

  useEffect(() => {
    if (!isFetchingScripts && !submittingSave && unitId !== undefined && unitId !== null) {
      startRerender();
      setScriptToSave({});
    }
  }, [unitId, submittingSave]);

  useEffect(() => {
    cleanup();
    if (unitId && isOpenSlidingPane) {
      fetchScriptsInterval.current = setInterval(() => {
        startRerender();
      }, 10000);
    }
  }, [isOpenSlidingPane, unitId]);

  useEffect(() => {
    if (isOpenSlidingPane && containerRef.current)
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, [isOpenSlidingPane, scriptsChats]);

  useEffect(() => {
    if (!isFetchingScripts && firstLoad) setFirstLoad(false);
  }, [isFetchingScripts]);

  return (
    <SlidingPane
      className="custom-sliding-pane custom-sliding-pane-no-padding"
      overlayClassName="custom-overlay-sliding-pane"
      isOpen={isOpenSlidingPane}
      title={intl.formatMessage({ id: 'units.sendScripts' })}
      subtitle={`${intl.formatMessage({ id: 'units.unit' })}: ${unitName}`}
      width={'45rem'}
      onRequestClose={() => {
        closeSlidingPaneSendScripts();
        reset();
      }}
    >
      <>
        {firstLoad && (
          <div className="d-flex justify-content-center my-1">
            <Spinner color="primary" />
          </div>
        )}
        {scriptsChats && scriptsChats?.length === 0 && !isFetchingScripts && (
          <div className="misc-inner p-2 p-sm-3">
            <div className="w-100 text-center">
              <h2 className="mb-1">
                <FormattedMessage id="units.noScripts"></FormattedMessage>
              </h2>
            </div>
          </div>
        )}
        {scriptsChats?.length > 0 && (
          <>
            <div ref={containerRef} className={'chat-container'}>
              {scriptsChats.map((script, i) => (
                <div
                  key={i}
                  className={`message-wrapper ${
                    script.userId === authUserId ? 'user-message' : 'other-message'
                  }`}
                >
                  <Icon icon={'Check'} size={16} color={!script.sentDate ? secondary : primary} />
                  <div
                    className={`message-bubble ${
                      script.userId === authUserId ? 'user-bubble' : 'other-user-bubble'
                    }`}
                  >
                    <div className={'message-header-content'}>
                      <Avatar
                        // @ts-ignore unreachable error
                        img={script?.userImageUrl ? script?.userImageUrl : undefined}
                        color="light-primary"
                        className={'mr-25'}
                        imgWidth={38}
                        imgHeight={38}
                        content={script.completeName == null ? '' : script.completeName}
                        initials
                      />
                      <div className={'message-header-text'}>
                        <span className={'message-username'}>{script.userName}</span>
                        <span className={'message-date'}>
                          {moment(script.scriptDate).format('lll')}
                        </span>
                      </div>
                      <div className={'message-status'}>
                        {script.isProcessed !== null && getScriptStatus(script.isProcessed)}
                      </div>
                    </div>
                    <div className={'message-subheading-content'}>
                      <span>{`${intl.formatMessage({ id: 'forms.type' })}: ${
                        script.scriptTypeDescription
                      }`}</span>
                      <span className="text-divider">•</span>
                      <span>{`${intl.formatMessage({ id: 'units.action' })}: ${
                        script.deviceActionDescription
                      }`}</span>
                    </div>
                    <Divider style={{ margin: '0.8rem 0rem' }} />
                    <div className={'message-body-content'}>
                      <span>{script.scriptText}</span>
                    </div>
                  </div>
                </div>
              ))}
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
            </div>
          </>
        )}
        <Row className={'footer-slide-pane-comments'}>
          <Col sm="10">
            <Controller
              control={control}
              name="script"
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextInput
                  field={field}
                  fieldstate={fieldState}
                  type={'textarea'}
                  required={true}
                  id={'scriptInput'}
                  maxLength={UI_ONLY_MAX_LENGTHS.DEVICE_SCRIPT}
                />
              )}
            />
          </Col>
          <Col>
            <Button.Ripple
              className={'mb-1'}
              color="primary"
              onClick={handleSubmit((values) => {
                setScriptToSave({
                  unitId: unitId,
                  script: values?.script
                });
                setExecuteSave(!executeSave);
                reset();
              })}
              disabled={
                newScript == null || newScript == '' || (newScript && newScript?.length == 0)
              }
            >
              <Send size={14} />
            </Button.Ripple>
          </Col>
        </Row>
      </>
    </SlidingPane>
  );
};

export default SlidingPaneUnitSendScripts;
